import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  BookOpen, FileText, Check, BarChart2, FileCheck, Zap, Award
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const TestSeriesInfoPage = () => {
  const features = [
    {
      icon: FileText,
      title: 'Real Exam Papers',
      description: 'Practice with actual board exam patterns and difficulty levels.'
    },
    {
      icon: FileCheck,
      title: 'Expert Evaluation',
      description: 'Detailed manual checking with personalized feedback on your answers.'
    },
    {
      icon: BarChart2,
      title: 'Performance Analytics',
      description: 'Track your progress with comprehensive test analytics and insights.'
    },
    {
      icon: BookOpen,
      title: 'Exam Strategies',
      description: 'Learn proven techniques to maximize your score in board exams.'
    }
  ];

  const pricingPlans = [
    {
      name: 'Mini Series',
      price: '₹1,499',
      description: 'Essential practice with expert feedback',
      features: [
        '5 full syllabus descriptive tests',
        'Manual evaluation with detailed comments',
        'Performance & progress tracking',
        'Feedback via written report',
        'Email support for test-related queries'
      ],
      popular: false
    },
    {
      name: 'Full Series',
      price: '₹2,499',
      description: 'Complete preparation package',
      features: [
        'Comprehensive 10-test series',
        'Covers full syllabus in detail',
        'Includes all Mini Series benefits',
        'Two 45-minute exam prep sessions',
        'Expert guidance on exam techniques',
        'Model answers for all questions',
        'Priority email & WhatsApp support'
      ],
      popular: true,
      ribbon: 'Best Value'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Board Exam Test Series - Physics Notebook</title>
        <meta name="description" content="Practice with real board exam papers and get detailed feedback to improve your scores. Choose between our Mini Series or Full Series test packages." />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-4xl md:text-5xl font-semibold text-gray-900 mb-4"
          >
            Board Exam Test Series
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 mb-6"
          >
            Practice Real Papers. Get Real Feedback.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-2xl font-semibold text-[#123B7A]"
          >
            Starts from <span className="line-through text-gray-500">₹1,999</span> ₹1,499
          </motion.p>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-semibold text-gray-900 mb-4">Choose Your Plan</h2>
            <div className="w-20 h-1 bg-[#123B7A] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={`rounded-xl shadow-lg overflow-hidden border-2 ${
                  plan.popular ? 'border-blue-500' : 'border-gray-200'
                }`}
              >
                {plan.ribbon && (
                  <div className="bg-[#123B7A] text-white text-center py-2 text-sm font-medium">
                    {plan.ribbon}
                  </div>
                )}
                <div className="p-6">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-4">{plan.description}</p>
                    <div className="mb-6">
                      <span className="text-4xl font-extrabold text-gray-900">{plan.price}</span>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-semibold text-gray-900 mb-4">What's Included</h2>
            <div className="w-20 h-1 bg-[#123B7A] mx-auto mb-8"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md"
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <Icon className="w-6 h-6 text-[#123B7A]" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TestSeriesInfoPage;